import { useState } from 'react';
import { Smartphone, ShieldCheck, Copy, Check, QrCode, KeyRound, Loader2 } from 'lucide-react';
import PageHeader from '../../components/settings/PageHeader';
import SettingCard from '../../components/settings/SettingCard';
import { useSettingsStore } from '../../store/settingsStore';
import { cn } from '../../utils/helpers';

// A stable mock secret for the demo enrollment flow.
const MOCK_SECRET = 'JBSW Y3DP EHPK 3PXP';

export default function TwoFactor() {
  const { data, saveSecurity, saving } = useSettingsStore();
  const enabled = data?.security.twoFactorEnabled ?? false;

  const [step, setStep] = useState<'idle' | 'setup' | 'verify'>('idle');
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const copySecret = async () => {
    await navigator.clipboard.writeText(MOCK_SECRET.replace(/\s/g, ''));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const verify = async () => {
    if (code.replace(/\s/g, '').length !== 6 || !/^\d+$/.test(code.replace(/\s/g, ''))) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setError(null);
    await saveSecurity({ twoFactorEnabled: true, twoFactorMethod: 'app' });
    setStep('idle');
    setCode('');
  };

  const disable = async () => {
    await saveSecurity({ twoFactorEnabled: false, twoFactorMethod: null });
  };

  return (
    <div className="space-y-6 max-w-xl">
      <PageHeader title="Two-factor authentication" subtitle="Require a one-time code in addition to your password" icon={Smartphone} backTo="/app/settings/security" />

      {/* Current status */}
      <SettingCard>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className={cn('w-11 h-11 rounded-2xl flex items-center justify-center', enabled ? 'bg-green-50 text-green-600' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400')}>
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold">{enabled ? 'Two-factor is on' : 'Two-factor is off'}</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">{enabled ? 'Authenticator app' : 'Your account is protected by password only'}</p>
            </div>
          </div>
          {enabled && (
            <button onClick={disable} disabled={saving} className="text-xs font-semibold text-rose-600 dark:text-rose-400 rounded-xl px-3 py-2 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Turn off'}
            </button>
          )}
        </div>
      </SettingCard>

      {!enabled && step === 'idle' && (
        <SettingCard title="How it works" flush>
          <Step n={1} title="Install an authenticator" desc="Use Google Authenticator, Authy, or 1Password." />
          <Step n={2} title="Scan the QR code" desc="Link Messego to your authenticator app." />
          <Step n={3} title="Enter the 6-digit code" desc="Confirm to turn on protection." />
          <div className="px-5 py-4">
            <button onClick={() => setStep('setup')} className="w-full bg-neutral-900 text-white py-3 rounded-xl font-semibold hover:bg-neutral-800 transition-colors">
              Set up two-factor
            </button>
          </div>
        </SettingCard>
      )}

      {!enabled && step === 'setup' && (
        <SettingCard title="Scan or enter the key" icon={QrCode}>
          <div className="flex flex-col items-center gap-4">
            <div className="w-44 h-44 rounded-2xl bg-neutral-900 flex items-center justify-center">
              {/* Placeholder QR — a real flow renders an otpauth:// QR here. */}
              <QrCode className="w-24 h-24 text-white" />
            </div>
            <button onClick={copySecret} className="flex items-center gap-2 rounded-xl border border-neutral-200 dark:border-neutral-800 px-4 py-2 font-mono text-sm hover:bg-neutral-50 dark:hover:bg-neutral-950">
              {MOCK_SECRET}
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-neutral-400 dark:text-neutral-500" />}
            </button>
            <button onClick={() => setStep('verify')} className="w-full bg-neutral-900 text-white py-3 rounded-xl font-semibold hover:bg-neutral-800 transition-colors">
              Continue
            </button>
          </div>
        </SettingCard>
      )}

      {!enabled && step === 'verify' && (
        <SettingCard title="Enter your code" icon={KeyRound}>
          <div className="space-y-4">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
              inputMode="numeric"
              placeholder="000000"
              className="w-full text-center tracking-[0.5em] font-mono text-2xl px-4 py-4 rounded-xl border border-neutral-200 dark:border-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-900/10"
            />
            {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setStep('setup')} className="flex-1 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 font-semibold text-sm hover:bg-neutral-50 dark:hover:bg-neutral-950">Back</button>
              <button onClick={verify} disabled={saving} className="flex-1 bg-neutral-900 text-white py-3 rounded-xl font-semibold hover:bg-neutral-800 transition-colors disabled:opacity-60">
                {saving ? 'Verifying…' : 'Turn on'}
              </button>
            </div>
          </div>
        </SettingCard>
      )}
    </div>
  );
}

function Step({ n, title, desc }: { n: number; title: string; desc: string }) {
  return (
    <div className="flex items-start gap-3.5 px-5 py-4 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
      <div className="w-7 h-7 rounded-full bg-neutral-900 text-white text-xs font-bold flex items-center justify-center shrink-0">{n}</div>
      <div>
        <p className="font-semibold text-sm">{title}</p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">{desc}</p>
      </div>
    </div>
  );
}
